/**
 * The line of text above the crosshair naming what interact will do — "Open", "Pick
 * up", "Drop". On desktop it also teaches the bindings; on touch the contextual button
 * carries the label instead, so the binding hint is suppressed there.
 *
 * Subscribes to discrete store state only (focus changes, not frames), so it renders a
 * handful of times a minute, not sixty times a second.
 */

import { actionLabel, availableAction } from '../game/interaction';
import { canStow } from '../game/pocket';
import { useGameStore } from '../store/gameStore';

type Props = {
  /** True on desktop, where keys exist to be taught. */
  showBinding: boolean;
};

export function InteractionPrompt({ showBinding }: Props) {
  const focus = useGameStore((store) => store.focus);
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  const carriedObjectKind = useGameStore((store) => store.carriedObjectKind);
  const doorStates = useGameStore((store) => store.doorStates);
  const roomLights = useGameStore((store) => store.roomLights);
  const attachments = useGameStore((store) => store.attachments);
  const pocket = useGameStore((store) => store.pocket);
  const profile = useGameStore((store) => store.profile);

  const action = availableAction(focus, {
    carriedObjectId,
    carriedObjectKind,
    doorStates,
    roomLights,
    attachments,
  });
  if (!action) return null;

  // The stow key is taught wherever it would work, next to the interact key, and the
  // condition is the touch button's own (design D2): the two devices must not disagree
  // about when the pocket is on offer. Nothing is said when it would refuse — a hint for
  // a control that will not act teaches the wrong rule (design D3). The profile is part
  // of the same question: a game may have no pocket at all (`per-game-pocket` D3).
  const stowable = showBinding && canStow(carriedObjectKind, pocket, profile);

  return (
    <div className="interaction-prompt">
      <span className="interaction-prompt__action">{actionLabel(action)}</span>
      {showBinding && <kbd className="interaction-prompt__key">E</kbd>}
      {showBinding && (
        // Clicking acts on the target with empty hands and throws with full ones, so the
        // hint has to say which — "or click" beside "Open" would be a lie while carrying.
        // A table is the third case: the press does nothing at all and the *hold* drags,
        // which is the one thing a player cannot guess from a label.
        <span className="interaction-prompt__hint">
          {action.verb === 'drag'
            ? 'hold to drag'
            : action.verb === 'drop'
              ? 'Click to throw'
              : 'or click'}
        </span>
      )}
      {stowable && (
        <span className="interaction-prompt__stow">
          <kbd className="interaction-prompt__key">F</kbd> Stow
        </span>
      )}
    </div>
  );
}
